export interface TranslatedPathnames {
  language: string;
  pathnames?: string;
}

export const ALL_LANGUAGES = [
  { code: 'en', label: 'EN', href: '/en/' },
  { code: 'de', label: 'DE', href: '/de/' },
  { code: 'fr', label: 'FR', href: '/fr/' },
  { code: 'it', label: 'IT', href: '/it/' },
  { code: 'ja', label: 'JA', href: '/ja/' },
  { code: 'es', label: 'ES', href: '/es/' },
] as const;

/**
 * Extracts pattern from a pathname line
 * Lines starting with # or 'if' are ignored
 */
function extractPatternFromLine(line: string): string | null {
  if (line.startsWith('#') || line.startsWith('if')) return null;

  // Extract the pattern between single quotes
  const match = line.match(/'([^']+)'/);
  if (!match || !match[1]) return null;

  let pattern: string = match[1];
  if (pattern.startsWith('~/')) {
    pattern = pattern.replace(/^~\//, '/');
  }

  return pattern;
}

/**
 * Tests if a path matches a regex pattern
 */
function testPathAgainstPattern(path: string, pattern: string): boolean {
  try {
    const regex = new RegExp(pattern);
    return regex.test(path);
  } catch (e) {
    console.warn('Invalid regex pattern:', pattern, e);
    return false;
  }
}

/**
 * Determines which languages a given path is translated into
 * @param currentPath - The current pathname (e.g., '/en/products')
 * @param translationDocs - Array of translation pathname documents from Sanity
 * @returns Array of language codes that the page is translated into
 */
export function getAvailableLanguagesForPath(
  currentPath: string,
  translationDocs: TranslatedPathnames[]
): string[] {
  const availableLanguages: string[] = [];

  translationDocs.forEach((doc) => {
    const { language, pathnames } = doc;
    const lines = pathnames?.split('\n') || [];

    for (const line of lines) {
      const pattern = extractPatternFromLine(line);
      if (!pattern) continue;

      // Replace the language code in the current path with the one from this doc
      const testPath = currentPath.replace(/^\/[^/]+/, `/${language}`);

      if (testPathAgainstPattern(testPath, pattern)) {
        availableLanguages.push(language);
        break; // Move to next language doc
      }
    }
  });

  return availableLanguages;
}

/**
 * Generates all language variants of a path
 * @param basePath - The base pathname
 * @param translationDocs - Array of translation pathname documents
 * @returns Array of language codes that the page is actually translated into
 */
export function getLanguageVariants(
  basePath: string,
  translationDocs: TranslatedPathnames[]
): string[] {
  const availableLanguages = getAvailableLanguagesForPath(basePath, translationDocs);

  // Only include languages the page is actually translated into
  // English is included only if the page has an English version
  const languages: string[] = [];

  // Add English by default (all pages start as English)
  languages.push('en');

  // Add other languages only if they're in the translation docs
  availableLanguages.forEach((lang) => {
    if (lang !== 'en' && !languages.includes(lang)) {
      languages.push(lang);
    }
  });

  return languages;
}

/**
 * Gets pages filtered for a specific locale
 * @param basePath - The base pathname (e.g., '/en/products')
 * @param translationDocs - Array of translation pathname documents
 * @param targetLocale - The target locale code (e.g., 'es', 'de')
 * @returns true if the page is available in the target locale
 */
export function isPageAvailableInLocale(
  basePath: string,
  translationDocs: TranslatedPathnames[],
  targetLocale: string
): boolean {
  // English is always available (all pages start as English)
  if (targetLocale === 'en') {
    return true;
  }

  // For other locales, check if page is translated
  const availableLanguages = getAvailableLanguagesForPath(basePath, translationDocs);
  return availableLanguages.includes(targetLocale);
}

/**
 * Replaces the language code in a URL path
 * @param path - The current path (e.g., '/en/products')
 * @param newLang - The new language code
 * @returns The path with replaced language code
 */
export function replaceLanguageInPath(path: string, newLang: string): string {
  return path.replace(/^\/[^/]+/, `/${newLang}`);
}

/**
 * Chech if a given language code is supported
 * @param langCode - The language code to check
 * @returns true if the language code is supported
 */
export function isSupportedLanguageCode(langCode: string): boolean {
  return ALL_LANGUAGES.some((lang) => lang.code === langCode);
}

/**
 * Resolve a route language value that may be a string or array.
 * Falls back to English when language is missing.
 */
export const getRouteLanguage = (routeLang: string | string[] | undefined): string => {
  if (Array.isArray(routeLang)) {
    return routeLang[0] || 'en';
  }

  return routeLang || 'en';
};

export type ReplaceLanguageInUrlOptions = {
  // When true, a language segment is prepended if the URL has no language prefix.
  insertWhenMissing?: boolean;
};

/**
 * Replaces (or inserts) the leading language segment in an absolute URL.
 *
 * Examples:
 * - `https://site.com/en/pricing` + `de` => `https://site.com/de/pricing`
 * - `https://site.com/pricing` + `fr` with `insertWhenMissing: true`
 *   => `https://site.com/fr/pricing`
 */
export function replaceLanguageInUrl(
  fullUrl: string,
  newLang: string,
  options: ReplaceLanguageInUrlOptions = {}
): string {
  const url = new URL(fullUrl);
  // Preserve original slash style (`/path` vs `/path/`) after mutation.
  const hasTrailingSlash = url.pathname.endsWith('/');
  const segments = url.pathname.split('/').filter(Boolean);

  const hasLanguagePrefix =
    segments.length > 0 &&
    /^[a-z]{2}$/.test(segments[0] || '') &&
    isSupportedLanguageCode(segments[0]!);

  if (hasLanguagePrefix) {
    segments[0] = newLang;
  } else if (options.insertWhenMissing) {
    segments.unshift(newLang);
  } else {
    // Leave URL unchanged when there's no language prefix and insertion is not requested.
    return url.toString();
  }

  url.pathname = '/' + segments.join('/');
  url.pathname += hasTrailingSlash ? '/' : '';
  return url.toString();
}
